import React, {useCallback} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {Button, Modal, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {WindowModeType} from 'types'
import {useWindowMode} from 'windows'
import {GiftshopTemplateSearchCreationModalProps} from './GiftShopTemplateSearchCreationModalProps'

interface StyledModalProps {
  mode: WindowModeType
}

const StyledConfirmationModal = styled.div<StyledModalProps>`
  width: ${({mode}) =>
    mode === 'mobile' ? convertUnit(275) : convertUnit(400)};
  background-color: ${({theme}) => theme.white_1};
  border-radius: ${convertUnit(8)};
  position: relative;
  transition: all 5s ease-in-out;
  z-index: 5;
`

const StyledModalButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: calc(100% - ${convertUnit(40)});
  justify-content: right;
  padding: ${convertUnit(20)};
`

const StyledTitle = styled(Paragraph)`
  text-align: center;
`

const StyledButton = styled(Button)`
  flex: 1;
`

const StyledListContainer = styled.div`
  height: ${convertUnit(400)};
  width: 100%;
  border-radius: ${convertUnit(8)};
  display: flex;
  align-items: center;
  flex-direction: column;
  box-sizing: border-box;
  margin-bottom: ${convertUnit(15)};
  overflow-y: auto;
`

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({theme}) => theme.gray_1};
  padding: ${convertUnit(25)};
`

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - ${convertUnit(40)});
  border-bottom: solid 1px ${({theme}) => theme.white_4};
  padding: ${convertUnit(8)} ${convertUnit(20)};
`

const StyledOverflowTitle = styled(Paragraph)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export default function GiftshopProfileSearchCreationContentFileModal({
  visible,
  toggleModal,
  files,
  foundSignatures,
}: GiftshopTemplateSearchCreationModalProps) {
  const {translate} = useTranslation()
  const mode = useWindowMode()

  const handleRenderItem = useCallback(
    (file: File, signature: string | undefined, index: number) => (
      <StyledContentContainer key={index}>
        <StyledOverflowTitle fontSize="m" fontWeight="medium" color="black">
          {file.name}
        </StyledOverflowTitle>
        <Paragraph fontSize="s" fontWeight="medium" color="primary_5">
          {translate('giftShop:searchCreationByFileMatches', {
            count: foundSignatures.filter((sign) => sign === signature).length,
          })}
        </Paragraph>
      </StyledContentContainer>
    ),
    [foundSignatures, translate],
  )

  return (
    <Modal
      visible={visible}
      onBackdropPress={() => {
        toggleModal()
      }}>
      <StyledConfirmationModal mode={mode}>
        <StyledTitleContainer>
          <StyledTitle fontWeight="bold" fontSize="xl">
            {translate('giftShop:searchCreationByFileModalTitle')}
          </StyledTitle>
          <Paragraph>
            {translate('giftShop:searchCreationByFileModalCount', {
              count: files.length,
            })}
          </Paragraph>
        </StyledTitleContainer>
        <StyledListContainer>
          {files.map(({file, signature}, index) =>
            handleRenderItem(file, signature, index),
          )}
        </StyledListContainer>

        <StyledModalButtonContainer>
          <StyledButton
            label={translate('global:cancel')}
            width={convertUnit(125)}
            backgroundHoverColor="white_3"
            backgroundColor="white_2"
            color="primary_5"
            onClick={() => {
              toggleModal()
            }}
          />
        </StyledModalButtonContainer>
      </StyledConfirmationModal>
    </Modal>
  )
}
